import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { ThemeSettings } from "./ThemeSettings";
import { Branding } from "./Branding";
import { WelcomePageTab } from "./WelcomePage";
import { AlertSettingsTab } from "./AlertSettingsTab";
import WebAppConfiguration from "./WebAppConfiguration";
import NotificationManager from "../../../components/notifications/NotificationManager";
import WatsonApi from "../../../backends/WatsonApi";
import SchoolBlocksLoadingIndicator from "../../../components/utilities/SchoolBlocksLoadingIndicator";
import { useStores } from "../../../stores/useStores";
import { runInAction } from "mobx";

export type ITheme = {
  id: number;
  folder: string;
  title: string;
  description: string;
  fields: IThemeField[];
};

type IThemeField = {
  default_value: string;
  name: string;
  title: string;
  field_type: string;
};

interface SubmitParameters {
  headerText: string;
  appearance: {
    source: string;
    staffDirectoryContactType: string | null;
    showSchoolFeedWidget: string;
    inheritSchoolFeedWidget: boolean;
    showWelcomePage: boolean;
    hideHeaderTitle: boolean;
    hideLoginButton: boolean;
    themeID: number;
    themes: {
      [selectedThemeId: number]: {
        [field: string]: any;
      };
    };
  };
  webapp: {
    enabled: boolean;
  };
  alert: {
    backgroundColor: string;
    fontColor: string;
  };
}

export function AppearanceTab() {
  const { organizationStore } = useStores();

  const { data, isLoading } = useSWR(
    "fetch-themes",
    async () => {
      try {
        const client = await WatsonApi();
        const response = await client.apis.themes.themes_list({});
        return JSON.parse(response.data);
      } catch (e) {
        console.error(e);
        return {
          message: "Error fetching theme data.",
        };
      }
    },
    {
      revalidateOnMount: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  const initialThemeId =
    organizationStore.organization.json_data.settings.appearance.themeID;
  const initialThemeSettings = {
    ...organizationStore.organization.json_data.settings.appearance.themes[
      initialThemeId
    ],
  };
  const hasSaved = useRef(false);

  // branding settings
  const [hideHeaderTitle, setHideHeaderTitle] = useState<boolean>(
    organizationStore.organization.json_data.settings.appearance
      .hideHeaderTitle,
  );
  const [hideHeaderLoginButton, setHideHeaderLoginButton] = useState<boolean>(
    organizationStore.organization.json_data.settings.appearance
      .hideLoginButton,
  );
  const [staffDirectoryContactType, setStaffDirectoryContactType] = useState<
    string | null
  >(
    organizationStore.organization.json_data.settings.appearance
      .staffDirectoryContactType,
  );
  const [showSchoolFeedWidget, setShowSchoolFeedWidget] = useState<string>(
    organizationStore.organization.json_data.settings.appearance
      .showSchoolFeedWidget,
  );
  const [inheritSchoolFeedWidget, setInheritSchoolFeedWidget] =
    useState<boolean>(
      typeof organizationStore.organization.json_data.settings.appearance
        .inheritSchoolFeedWidget === "boolean"
        ? organizationStore.organization.json_data.settings.appearance
            .inheritSchoolFeedWidget
        : true,
    );

  // welcome page settings
  const [showWelcomePage, setShowWelcomePage] = useState<boolean>(
    organizationStore.organization.json_data.settings.appearance
      .showWelcomePage,
  );
  const [taglineText, setTaglineText] = useState<string>(
    organizationStore.organization.json_data.settings.headerText,
  );

  // web app settings
  const [webAppEnabled, setWebAppEnabled] = useState<boolean>(
    !!organizationStore.organization.json_data.settings.webapp.enabled,
  );

  // theme settings
  const [selectedThemeId, setSelectedThemeId] = useState<number>(
    organizationStore.organization.json_data.settings.appearance.themeID,
  );
  const [themeFieldSettings, setThemeFieldSettings] = useState(
    organizationStore.organization.json_data.settings.appearance.themes,
  );

  // alert settings
  const [backgroundColor, setBackgroundColor] = useState(
    organizationStore.organization.json_data.settings.alert.backgroundColor,
  );
  const [fontColor, setFontColor] = useState(
    organizationStore.organization.json_data.settings.alert.fontColor,
  );
  const [backgroundImage, setBackgroundImage] = useState(
    () => organizationStore.organization.backgroundImage,
  );

  useEffect(() => {
    return () => {
      if (!hasSaved.current) {
        organizationStore.updateThemeSettings(
          initialThemeSettings,
          initialThemeId,
        );
      }
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();

    const data: SubmitParameters = {
      headerText: taglineText,
      appearance: {
        source:
          organizationStore.organization.json_data.settings.appearance.source,
        staffDirectoryContactType: staffDirectoryContactType,
        showSchoolFeedWidget: showSchoolFeedWidget,
        inheritSchoolFeedWidget: inheritSchoolFeedWidget,
        showWelcomePage: showWelcomePage,
        hideHeaderTitle: hideHeaderTitle,
        hideLoginButton: hideHeaderLoginButton,
        themeID: selectedThemeId,
        themes: themeFieldSettings[selectedThemeId],
      },
      webapp: {
        enabled: webAppEnabled,
      },
      alert: {
        backgroundColor: backgroundColor,
        fontColor: fontColor,
      },
    };

    try {
      const client = await WatsonApi();
      const response = await client.apis.settings.settings_update({
        id: organizationStore.currentOrganization.id,
        data: data,
      });

      const settings = response.body.settings;
      if (settings) {
        runInAction(() => {
          organizationStore.organization.json_data.settings = settings;
        });
        hasSaved.current = true;
        NotificationManager.success("Settings successfully updated!");
      }
    } catch (e) {
      switch (e.statusCode) {
        case 400:
          NotificationManager.error("Bad Request: Settings not updated.");
          break;
        case 404:
          NotificationManager.error(
            "Not Found: Organization settings not found.",
          );
          break;
        case 500:
          NotificationManager.error(
            "Internal Server Error: Settings not updated.",
          );
          break;
        default:
          NotificationManager.error("Unknown Error: Settings not updated.");
          break;
      }
    }
  }

  return (
    <div>
      <div className="sb-settings-block-content-heading">
        <h4>Appearance</h4>
      </div>
      <form id="district-appearance-form" onSubmit={handleSubmit}>
        <Branding
          hideHeaderTitle={hideHeaderTitle}
          setHideHeaderTitle={setHideHeaderTitle}
          hideHeaderLoginButton={hideHeaderLoginButton}
          setHideHeaderLoginButton={setHideHeaderLoginButton}
          staffDirectoryContactType={staffDirectoryContactType}
          setStaffDirectoryContactType={setStaffDirectoryContactType}
          showSchoolFeedWidget={showSchoolFeedWidget}
          setShowSchoolFeedWidget={setShowSchoolFeedWidget}
          inheritSchoolFeedWidget={inheritSchoolFeedWidget}
          setInheritSchoolFeedWidget={setInheritSchoolFeedWidget}
        />
        <WelcomePageTab
          showWelcomePage={showWelcomePage}
          setShowWelcomePage={setShowWelcomePage}
          taglineText={taglineText}
          setTaglineText={setTaglineText}
        />
        <WebAppConfiguration
          webAppEnabled={webAppEnabled}
          setWebAppEnabled={setWebAppEnabled}
        />
        {isLoading ? (
          <div style={{ textAlign: "center" }}>
            <SchoolBlocksLoadingIndicator fill={"var(--fontColor)"} />
          </div>
        ) : (
          <ThemeSettings
            themeOptions={data}
            selectedThemeId={selectedThemeId}
            setSelectedThemeId={setSelectedThemeId}
            themeFieldSettings={themeFieldSettings}
            setThemeFieldSettings={setThemeFieldSettings}
            backgroundImage={backgroundImage}
            setBackgroundImage={setBackgroundImage}
          />
        )}
        <AlertSettingsTab
          backgroundColor={backgroundColor}
          setBackgroundColor={setBackgroundColor}
          fontColor={fontColor}
          setFontColor={setFontColor}
        />
        <div className="sb-settings-block-footer">
          <button
            type="submit"
            className="btn sb-organization-color-element-bg"
            name="submit"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
}
